import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Hash, LifeBuoy, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTenant } from "@/context/tenant-context";
import {
  useChatNotifications,
  markSessionReadAndRefresh,
} from "@/hooks/use-chat-notifications";
import {
  chatNotificationsApi,
  type ChatNotificationItem,
} from "@/api/chat-notifications";
import {
  userNotificationsApi,
  type UserNotification,
} from "@/api/support-tickets";

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(1, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function NotificationRow({
  item,
  onOpen,
}: {
  item: ChatNotificationItem;
  onOpen: () => void;
}) {
  return (
    <button
      onClick={onOpen}
      className="group flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left hover:bg-surface-hover"
    >
      <div
        className={cn(
          "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
          item.directToMe
            ? "bg-accent/15 text-accent"
            : "bg-surface-hover text-text-muted",
        )}
      >
        {item.channelShared ? <Hash size={14} /> : <MessageSquare size={14} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate text-[13px] font-semibold text-text-primary">
            {item.agentName}
          </span>
          {item.channelName && (
            <span className="truncate text-[11.5px] text-text-muted">
              · #{item.channelName}
            </span>
          )}
          <span className="ml-auto shrink-0 text-[11px] text-text-muted">
            {formatRelative(item.createdAt)}
          </span>
        </div>
        <p className="m-0 mt-0.5 line-clamp-2 text-[12.5px] text-text-secondary">
          {item.content}
        </p>
        {item.directToMe && (
          <span className="mt-1 inline-block rounded-full bg-accent/10 px-2 py-[1px] text-[10px] font-semibold uppercase tracking-[0.05em] text-accent">
            Direct to you
          </span>
        )}
      </div>
    </button>
  );
}

export function NotificationsBell() {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { items, unreadCount: chatUnread } = useChatNotifications();

  // Generic platform notifications (support ticket updates etc.) — separate
  // feed from chat, merged into the same bell.
  const { data: platformFeed } = useQuery({
    queryKey: ["user-notifications"],
    queryFn: userNotificationsApi.list,
    refetchInterval: 30_000,
  });
  const platformItems = (platformFeed?.notifications ?? []).filter((n) => !n.read);
  const unreadCount = chatUnread + (platformFeed?.unreadCount ?? 0);

  const openPlatformItem = (n: UserNotification) => {
    void userNotificationsApi
      .markRead(n.id)
      .then(() => queryClient.invalidateQueries({ queryKey: ["user-notifications"] }))
      .catch(() => undefined);
    if (n.link) navigate(n.link);
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const openItem = (item: ChatNotificationItem) => {
    if (item.agentId) {
      // Target the workspace chat surface directly — going via the agent
      // detail page flashes the config tab before redirecting. Also use the
      // `sessionId` param that workspace-context reads (not `session`).
      const params = new URLSearchParams({
        agentId: item.agentId,
        sessionId: item.sessionId,
      });
      if (item.channelId) params.set("channelId", item.channelId);
      navigate(`/tenants/${tenantId}/workspace?${params.toString()}`);
    }
    void markSessionReadAndRefresh(item.sessionId);
    setOpen(false);
  };

  const markAllRead = async () => {
    try {
      await chatNotificationsApi.markAllRead(tenantId);
      await userNotificationsApi.markAllRead();
    } catch {
      /* non-fatal; next poll will reconcile */
    }
    queryClient.invalidateQueries({ queryKey: ["chat-notifications", tenantId] });
    queryClient.invalidateQueries({ queryKey: ["user-notifications"] });
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        className={cn(
          "relative inline-flex h-8 w-8 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary",
          open && "bg-surface-hover text-text-primary",
        )}
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold leading-none text-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-[calc(100%+6px)] z-50 flex w-[360px] max-w-[92vw] flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-xl"
          style={{ animation: "fade-in 0.16s ease-out" }}
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <span className="text-[13px] font-semibold text-text-primary">
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-[11.5px] text-text-muted hover:text-text-primary"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto p-1">
            {items.length === 0 && platformItems.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell size={20} className="mx-auto mb-2 text-text-muted" />
                <p className="m-0 text-[13px] font-medium text-text-primary">
                  You're all caught up
                </p>
                <p className="m-0 mt-1 text-[12px] text-text-muted">
                  New agent messages will appear here.
                </p>
              </div>
            ) : (
              <>
                {platformItems.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => openPlatformItem(n)}
                    className="group flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left hover:bg-surface-hover"
                  >
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent/15 text-accent">
                      <LifeBuoy size={14} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="m-0 text-[13px] font-semibold text-text-primary">{n.title}</p>
                      {n.body && (
                        <p className="m-0 mt-0.5 line-clamp-2 text-[12.5px] text-text-secondary">
                          {n.body}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
                {items.map((it) => (
                  <NotificationRow
                    key={it.messageId}
                    item={it}
                    onOpen={() => openItem(it)}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
